/**
 * Leaderboard - card design v3 with avatar + status, soft rows.
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";

const PALETTE = ["#FC8019", "#7C4DFF", "#4CAF50", "#FF6B9D"];

function Avatar({ initials, color }) {
  return (
    <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <span style={{
        width: 40, height: 40, borderRadius: "50%",
        backgroundColor: color,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#FFFFFF", fontSize: 14, fontWeight: 700,
      }}>{initials}</span>
      {/* No status dot — we have no real-time presence data, so any "online"
          indicator would be fabricated. */}
    </span>
  );
}

/**
 * Leaderboard
 *
 *   <Leaderboard rows={[{ name, value, displayValue?, subtitle? }]} ... />
 *
 * Each row may optionally include a `subtitle` string sourced from real data
 * (e.g. "12 calls" / "3 active months" / "184 fans referred"). If omitted, no
 * sub-line is rendered. The component never fabricates "online now" / "active
 * this month" labels.
 */
export function Leaderboard({ title, subtitle, rows = [], rightLabel = "Earnings", format = (v) => v.toLocaleString("en-IN") }) {
  return (
    <div style={{
      backgroundColor: T.surface.card,
      borderRadius: 16,
      padding: 16,
      borderLeft: "4px solid " + T.brand.base,
      display: "flex", flexDirection: "column", gap: 14,
      fontFamily: T.font.sans,
      boxShadow: "0 1px 2px rgba(31,42,63,0.04)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>{subtitle}</div>}
        </div>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
          textTransform: "uppercase", color: T.text.muted,
        }}>{rightLabel}</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {rows.map((row, i) => {
          const initials = (row.name || "").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "?";
          const color = PALETTE[i % PALETTE.length];
          const display = row.displayValue != null ? row.displayValue : format(row.value || 0);
          return (
            <div key={i} style={{
              display: "grid",
              gridTemplateColumns: "16px 1fr auto",
              alignItems: "center",
              gap: 12,
              padding: "10px 6px",
              borderRadius: 12,
              transition: "background-color 120ms ease",
            }}>
              <span style={{
                fontSize: 13, fontWeight: 700,
                color: i < 3 ? T.brand.base : T.text.muted,
                fontVariantNumeric: "tabular-nums",
                textAlign: "right",
              }}>{i + 1}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <Avatar initials={initials} color={color} />
                <span style={{ minWidth: 0 }}>
                  <span style={{
                    display: "block",
                    fontSize: 15, fontWeight: 700, color: T.text.primary,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{row.name}{row.nameSuffix}</span>
                  {row.subtitle && (
                    <span style={{
                      display: "block",
                      fontSize: 12, color: T.text.muted, marginTop: 3,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{row.subtitle}</span>
                  )}
                  {row.subtitle2 && (
                    <span style={{
                      display: "block",
                      fontSize: 12, color: T.text.muted, marginTop: 2,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{row.subtitle2}</span>
                  )}
                  {row.subtitle3 && (
                    <span style={{
                      display: "block",
                      fontSize: 12, color: T.text.muted, marginTop: 2,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{row.subtitle3}</span>
                  )}
                  {row.subtitle4 && (
                    <span style={{
                      display: "block",
                      fontSize: 12, color: T.text.muted, marginTop: 2,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{row.subtitle4}</span>
                  )}
                </span>
              </span>
              <span title={row.displayTitle || undefined} style={{
                fontSize: 16, fontWeight: 700, color: T.text.primary,
                fontVariantNumeric: "tabular-nums",
                fontFeatureSettings: T.font.nums,
                textAlign: "right",
                whiteSpace: "nowrap",
                cursor: row.displayTitle ? "help" : undefined,
              }}>{display}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
